/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from "react"
import "./styles.scss"
import { useMultiCollapsible, useQuery, useTableColumnsManager } from "hooks"
import { useTranslation } from "react-i18next"
import { Buttons, Inputs, Shared } from "components"
import { useNavigate } from "react-router-dom"
import { reservationsColumns } from "config/constants"
import { mapSearchQuery, renderCell, xlsxExport } from "utilities"
import { useDispatch, useSelector } from "react-redux"
import { emit, setReservationsFields } from "actions"
import { debounce, groupBy, isEmpty } from "lodash"
import moment from "moment"

const Reservations = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const tabs = [{ label: t('reservations.tabToday'), value: 'today' }, { label: t('reservations.tabFuture'), value: 'future' }, { label: t('reservations.tabPast'), value: 'past' },]
    const views = [{ label: t('reservations.groupByHorological'), value: 'horological' }, { label: t('reservations.groupByEmployee'), value: 'employees' },]

    const { tab = tabs[0].value, view = views[0].value, handleUrlChange } = useQuery()

    const { filter, sort } = useQuery()
    const searchQuery = useMemo(() => ({ ...mapSearchQuery(sort, filter), period: tab, ...(tab !== 'past' && { pagination: false }) }), [filter, tab])

    const { reservations, page, nextPage, hasNextPage, stats } = useSelector(({ reservations }) => reservations)
    const { total, totalNet, totalReservations, vehicles } = stats || {}

    const fetch = ({ pageNumber, pagination = true, onSuccess = () => { } } = {}) => emit({
        action: 'reservation/browse',
        payload: { pageSizeLimit: 100, pageNumber: pageNumber || nextPage, stats: page > 1 ? stats : {}, pagination, ...searchQuery },
        startLoading: isEmpty(reservations) || nextPage !== 1,
        stopLoading: true,
        onSuccess: () => { localStorage.setItem('lastReservationsQuery', JSON.stringify(searchQuery)); onSuccess() }
    })
    const fetchDebounced = debounce(fetch, 300)

    const dispatch = useDispatch()
    useEffect(() => { fetch({ pageNumber: 1 }) }, [searchQuery])
    useEffect(() => { emit({ action: 'room/join', payload: { room: 'reservations' } }) }, [])
    useEffect(() => () => {
        emit({ action: 'room/leave', payload: { room: "reservations" } })
        dispatch(setReservationsFields({ nextPage: 1, hasNextPage: true }))
    }, [dispatch])

    const groupedByEmployees = useMemo(() => Object.entries(groupBy(reservations, ({ cart = {}, cartBySystem = {} }) => cart._id ?? cartBySystem._id)), [reservations])
    const groupedByDate = useMemo(() => Object.entries(Object.entries(groupBy(reservations, ({ reservationStart }) => moment(reservationStart).format('DD.MM.YYYY'))).reduce((acc, [key, values]) => ({ ...acc, [key]: Object.entries(groupBy(values, ({ cart = {}, cartBySystem = {} }) => cart._id ?? cartBySystem._id)) }), {})), [reservations])
    const { refs, openStates, toggle, closeAll } = useMultiCollapsible()
    useEffect(() => { closeAll() }, [tab, view])
    
    const { useColumnsManager, currentFields, handleFilterPresets } = useTableColumnsManager({ defaultFields: reservationsColumns, type: 'reservations' })



    return <div className="screen-reservations-container">
        <div className="screen-reservations-header row">
            <div className="tabs-container row">{tabs.map(({ value, label }) => <div key={value} className={`single-tab${tab === value ? ' active' : ''}`} onClick={() => handleUrlChange({ tab: value, ...(value === 'past' && { view: 'horological' }) })}>{label}</div>)}</div>
            {tab !== 'past' && <div className="views-container row">
                <span>{t('reservations.groupBy')}</span>
                <Inputs.RadioButtons options={views} value={view} onChange={(view) => handleUrlChange({ view })} />
            </div>}
        </div>
        <div className="screen-reservations-inner-container">
            <div className={`screen-reservations-content ${view}`}>
                {view === 'horological' && <Shared.Table
                    useColumnsManager={useColumnsManager}
                    columns={currentFields}
                    data={reservations}
                    renderCell={(reservation, field) => renderCell.reservations(reservation, field, { navigate, t })}
                    handlePagination={() => hasNextPage && fetchDebounced()}
                />}
                {tab === 'today' && view === 'employees' && groupedByEmployees.map(([key, values]) => {
                    const groupedByStatus = groupBy(values, 'status')
                    return <div key={key} className="employee-container">
                        <div className="row employee-header" onClick={() => toggle(key)}>
                            <p>{values[0]?.cart?.identifier ?? values[0]?.cartBySystem?.identifier}</p>
                            <p>{values.length - (groupedByStatus?.completed?.length ?? 0) - (groupedByStatus?.cancelled?.length ?? 0)} {t('reservations.active')}</p>
                            <p>{groupedByStatus?.completed?.length ?? 0} {t('reservations.completed')}</p>
                            <div className={`icon icon-arrow-down${openStates[key] ? ' rotate' : ''}`} />
                        </div>
                        <div
                            className={`employee-content`}
                            ref={(ref) => { if (ref) refs.current[key] = ref }}
                        >
                            <Shared.Table
                                useColumnsManager={useColumnsManager}
                                columns={currentFields}
                                data={values}
                                renderCell={(reservation, field) => renderCell.reservations(reservation, field, { navigate, t })}
                                handlePagination={() => hasNextPage && fetchDebounced()}
                            />
                        </div>
                    </div>
                })}
                {tab === 'future' && view === 'employees' && groupedByDate.map(([date, values]) => {
                    return <div key={date} className="employee-container">
                        <div className="row employee-header" onClick={() => toggle(date)}>
                            <p>{date}</p>
                            <div className={`icon icon-arrow-down${openStates[date] ? ' rotate' : ''}`} />
                        </div>
                        <div
                            className={`employee-content`}
                            ref={(ref) => { if (ref) refs.current[date] = ref }}
                        >
                            {values.map(([_id, values]) => {
                                const key = `${date}-${_id}`
                                const groupedByStatus = groupBy(values, 'status')
                                return <div key={key} className="employee-container secondary">
                                    <div className="row employee-header" onClick={() => toggle(key)}>
                                        <p>{values[0]?.cart?.identifier ?? values[0]?.cartBySystem?.identifier}</p>
                                        <p>{values.length - (groupedByStatus?.completed?.length ?? 0) - (groupedByStatus?.cancelled?.length ?? 0)} {t('reservations.active')}</p>
                                        <p>{groupedByStatus?.completed?.length ?? 0} {t('reservations.completed')}</p>
                                        <div className={`icon icon-arrow-down${openStates[key] ? ' rotate' : ''}`} />
                                    </div>
                                    <div
                                        className={`employee-content`}
                                        ref={(ref) => { if (ref) refs.current[key] = ref }}
                                    >
                                        <Shared.Table
                                            useColumnsManager={useColumnsManager}
                                            columns={currentFields}
                                            data={values}
                                            renderCell={(reservation, field) => renderCell.reservations(reservation, field, { navigate, t })}
                                            handlePagination={() => hasNextPage && fetchDebounced()}
                                        />
                                    </div>
                                </div>
                            })}
                        </div>
                    </div>
                })}
            </div>
            <div className="screen-reservations-footer row">
                <div className="row row-stats">
                    <p>{t('reservations.orders')}: <span>{totalReservations}</span></p>
                    <p>{t('reservations.totalAmount')}: <span>{total} {t('reservations.lv')}</span> ({t('reservations.netIncome')} <span>{totalNet} {t('reservations.lv')}</span>) </p>
                    <p>{t('reservations.totalVehicles')}: <span>{vehicles}</span></p>
                </div>
                <div className="row row-buttons">
                    <Buttons.Raised text={t('reservations.createReservation')} onClick={() => navigate(`/reservation/form`)} />
                    {handleFilterPresets()}
                    <div className="icon icon-export" onClick={() => fetch({
                        pagination: false,
                        onSuccess: (response) => xlsxExport({
                            fields: currentFields.toSpliced(13, 0, { value: 'address', label: t('reservation.address') }),
                            data: response.reservations,
                            render: renderCell.reservations,
                            fileName: `reservations`,
                            t
                        })
                    })} />
                </div>
            </div>
        </div>
    </div >
}

export default Reservations